'use client';

import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { useSearchParams } from 'next/navigation';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import { ProgressCheckList, type ChecklistItemData } from '@/ui-components/organisms/ProgressCheckList/ProgressCheckList';
import type { Document } from '@contentful/rich-text-types';
import styles from './progresschecklistgroup.module.scss';

interface Props {
    asset: SiteAssetGroup;
}

export function ProgressCheckListGroup({ asset }: Props) {
    const searchParams = useSearchParams();
    const signalRefreshError = searchParams?.get('signalRefreshError') === 'true';
    const username = searchParams?.get('usr') ?? undefined;

    const items: ChecklistItemData[] = (asset.fields.assets as SiteContentListItem[]).map((item, index) => {
        const fields = item.fields;
        const cta = fields.ctas?.[0];
        const isAppDownload = !!cta?.fields?.qr_code;

        const icon = fields.icon?.url ? (
            <div
                className={styles.iconBadge}
                // Fix #8: imageBgColorHex is already string | undefined, ?? undefined is a no-op
                style={{ backgroundColor: fields.icon.imageBgColorHex }}
            >
                <ImageWrapper src={fields.icon.url} alt={fields.icon.altText ?? ''} />
            </div>
        ) : undefined;

        const warningMessage = fields.warning_message;
        const hasWarningMessage =
            !!warningMessage && documentToPlainTextString(warningMessage).trim().length > 0;
        const isWarningActive = signalRefreshError && hasWarningMessage;
        const warning = isWarningActive
            ? {
                  label: <RenderRTE contentfulDoc={warningMessage} removeSpanTag />,
                  href: fields.text_link?.fields?.href,
              }
            : undefined;

        const base: ChecklistItemData = {
            icon,
            // Fix #7: use library function instead of custom helper
            title: documentToPlainTextString(fields.headline as Document),
            subtitle: fields.sub_headline
                ? documentToPlainTextString(fields.sub_headline as Document)
                : undefined,
            defaultChecked: index === 0 && !isWarningActive,
            warning,
        };

        if (isAppDownload) {
            return {
                ...base,
                linkType: 'app-download',
                qrCodeSrc: cta.fields.qr_code!.url,
                mobileHref: cta.fields.href,
            };
        }

        if (cta?.fields?.href) {
            return { ...base, href: cta.fields.href };
        }

        return base;
    });

    return <ProgressCheckList id={asset.cms_id} items={items} username={username} />;
}
